import { useEffect, useState } from "react";

// Tiny "Internet speed: high / medium / low / offline" indicator.
//
// Two sources, cheapest first:
// 1. The Network Information API (Chrome/Android, Samsung, Edge): the browser's
//    own estimate of effective connection type and downlink, kept live via its
//    "change" event. No traffic needed.
// 2. Elsewhere (iOS Safari, Firefox) we time a small request to our own server
//    every so often — the API is always tiny and never cached — and grade the
//    round trip. This measures what the app actually feels: latency to us.

type Level = "high" | "medium" | "low" | "offline";

type NetInfo = {
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  downlink?: number; // Mbit/s estimate
  rtt?: number; // ms estimate
  addEventListener?: (type: "change", cb: () => void) => void;
  removeEventListener?: (type: "change", cb: () => void) => void;
};
const netInfo = (): NetInfo | undefined => (navigator as unknown as { connection?: NetInfo }).connection;

const PROBE_MS = 30_000;
const PROBE_URL = "/api/version";

function levelFromInfo(c: NetInfo): Level | null {
  if (c.effectiveType === "4g" && (c.downlink === undefined || c.downlink >= 5)) return "high";
  if (c.effectiveType === "4g" || c.effectiveType === "3g") return "medium";
  if (c.effectiveType === "2g" || c.effectiveType === "slow-2g") return "low";
  return null;
}

async function probe(): Promise<Level> {
  const t0 = performance.now();
  try {
    const res = await fetch(`${PROBE_URL}?ping=${Date.now()}`, { cache: "no-store", signal: AbortSignal.timeout(6000) });
    if (!res.ok) return "low";
    const ms = performance.now() - t0;
    if (ms < 250) return "high";
    if (ms < 900) return "medium";
    return "low";
  } catch {
    return navigator.onLine ? "low" : "offline";
  }
}

const LABEL: Record<Level, string> = { high: "high", medium: "medium", low: "low", offline: "offline" };
const DOT: Record<Level, string> = { high: "bg-[#16a34a]", medium: "bg-honey", low: "bg-rose", offline: "bg-ink/40" };

export default function NetSpeed({ className = "" }: { className?: string }) {
  const [level, setLevel] = useState<Level | null>(null);

  useEffect(() => {
    let cancelled = false;
    const c = netInfo();

    const update = async () => {
      if (!navigator.onLine) return setLevel("offline");
      const fromInfo = c ? levelFromInfo(c) : null;
      const next = fromInfo ?? (await probe());
      if (!cancelled) setLevel(next);
    };

    update();
    const timer = setInterval(update, PROBE_MS);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    c?.addEventListener?.("change", update);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      c?.removeEventListener?.("change", update);
    };
  }, []);

  if (!level) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap ${className}`} title="Connection to the app server">
      <span className={`inline-block size-2 rounded-full ${DOT[level]}`} aria-hidden />
      Internet speed {LABEL[level]}
    </span>
  );
}
